import type {
  CompanyBasicResponse,
  IndustryClassificationCode,
  IndustryClassificationScheme,
  IndustryClassificationSourceScheme,
  NaceCodeItem,
} from '@/lib/backend-api';
import { euSmeDeclarationSchema, industryClassificationFieldsSchema } from '@/lib/ssr-schemas';

const SCHEME_ORDER: IndustryClassificationScheme[] = ['nace_rev_2_1', 'wz_2008', 'nace_rev_2', 'isic_rev_4'];
const SCHEME_LABELS: Record<IndustryClassificationSourceScheme, string> = {
  nace_rev_2_1: 'NACE Rev. 2.1',
  wz_2008: 'WZ 2008',
  nace_rev_2: 'NACE Rev. 2',
  isic_rev_4: 'ISIC Rev. 4',
  oenace_2025: 'ÖNACE 2025',
};

export function classificationSchemeLabel(scheme: IndustryClassificationSourceScheme): string {
  return SCHEME_LABELS[scheme];
}

function localizedTitle(titleDe: string | null | undefined, titleEn: string | null | undefined, language: string) {
  return language === 'en' ? titleEn || titleDe || undefined : titleDe || titleEn || undefined;
}

export function classificationCodeTitle(code: IndustryClassificationCode, language: string): string | undefined {
  return localizedTitle(code.title_de, code.title_en, language);
}

/** Only mapped codes get blocks; preserve tag order and never mutate backend data. */
export function industryClassificationBlocks(naceCodes: NaceCodeItem[] | null | undefined, language: string) {
  if (!Array.isArray(naceCodes)) return [];
  return naceCodes.flatMap((item) => {
    const parsed = industryClassificationFieldsSchema.safeParse(item);
    if (!parsed.success) return [];
    const { code, source_scheme, source_code, source_title_de, source_title_en, other_classifications } = parsed.data;
    const rows = SCHEME_ORDER.flatMap((scheme) =>
      (other_classifications ?? [])
        .filter((entry) => entry.scheme === scheme && entry.codes.length > 0)
        .map((entry) => ({
          scheme,
          label: classificationSchemeLabel(scheme),
          codes: entry.codes.slice(0, 4).map((candidate) => ({
            code: candidate.code,
            title: classificationCodeTitle(candidate, language),
          })),
          remaining: Math.max(0, entry.codes.length - 4),
          partial: entry.partial,
        })),
    );
    if (rows.length === 0) return [];
    const sourceTitle = localizedTitle(source_title_de, source_title_en, language);
    return [{
      code,
      rows,
      source: source_scheme
        ? {
            scheme: classificationSchemeLabel(source_scheme),
            code: source_code ?? '',
            ...(sourceTitle ? { title: sourceTitle } : {}),
          }
        : null,
    }];
  });
}

/** Missing or invalid declarations make no claim about the company's EU SME status. */
export function euSmeSentenceKey(declaration: CompanyBasicResponse['eu_sme_declaration']): string | null {
  const parsed = euSmeDeclarationSchema.safeParse(declaration);
  if (!parsed.success) return null;
  return parsed.data.is_sme ? 'companyDetail.euSme.isSme' : 'companyDetail.euSme.notSme';
}
